import { LookupFilter } from './lookup-filter';
import { GridFilter, GridOption } from './model';

type Props = {
  filter: GridFilter;
  value: string;
  disabled?: boolean;
  labels?: Record<string, string>;
  onChange: (value: string, options?: GridOption[]) => void;
};

export function FilterInput({
  filter,
  value,
  disabled,
  onChange,
  labels,
}: Props) {
  const label = `Фильтр: ${filter.label}`;
  if (filter.lookup || (filter.filterType === 'ids' && filter.options)) {
    return (
      <LookupFilter
        filter={filter}
        value={value}
        disabled={disabled}
        onChange={onChange}
        labels={labels}
      />
    );
  }
  if (filter.filterType === 'date-range') {
    const [from = '', to = ''] = value.split(',');
    const update = (start: string, end: string) =>
      onChange(start || end ? `${start},${end}` : '');
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="date"
          aria-label={`${label}: от`}
          disabled={disabled}
          value={from}
          max={to || undefined}
          onChange={(e) => update(e.target.value, to)}
        />
        <input
          type="date"
          aria-label={`${label}: до`}
          disabled={disabled}
          value={to}
          min={from || undefined}
          onChange={(e) => update(from, e.target.value)}
        />
      </div>
    );
  }
  if (filter.filterType === 'boolean') {
    return (
      <select
        aria-label={label}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Все</option>
        <option value="true">Да</option>
        <option value="false">Нет</option>
      </select>
    );
  }
  if (filter.options?.length) {
    const multiple = filter.filterType === 'ids';
    return (
      <select
        aria-label={label}
        disabled={disabled}
        multiple={multiple}
        size={multiple ? 3 : undefined}
        value={multiple ? value.split(',').filter(Boolean) : value}
        onChange={(e) =>
          onChange(
            multiple
              ? Array.from(e.target.selectedOptions)
                  .map((o) => o.value)
                  .filter(Boolean)
                  .join(',')
              : e.target.value
          )
        }
      >
        {!multiple && <option value="">Все</option>}
        {filter.filterType === 'nullable-integer' && (
          <option value="null">Не задан</option>
        )}
        {filter.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  const hint =
    filter.filterType === 'ids'
      ? 'ID через запятую: 1, 2'
      : filter.filterType === 'nullable-integer'
      ? 'ID или null (не задан)'
      : 'Фильтр…';
  return (
    <input
      aria-label={label}
      disabled={disabled}
      placeholder={hint}
      title={hint}
      inputMode={filter.filterType === 'integer' ? 'numeric' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function filterSummary(
  filter: GridFilter,
  value: string,
  labels: Record<string, string> = {}
): string {
  if (filter.filterType === 'boolean') {
    return value === 'true' ? 'Да' : 'Нет';
  }
  if (value === 'null' && filter.filterType === 'nullable-integer') {
    return 'Не задан';
  }
  if (filter.filterType === 'date-range') {
    return value
      .split(',')
      .map((v) => v || '…')
      .join(' — ');
  }
  return (filter.filterType === 'ids' ? value.split(',') : [value])
    .map(
      (v) => filter.options?.find((o) => o.value === v)?.label || labels[v] || v
    )
    .join(', ');
}
